import type { JSX } from 'preact';
import { t } from '../idle/terms.ts';
import { Icon } from './Icon.tsx';

export interface TabDef {
  id: string;
  label: string;
  icon: string;
}

export const HEDGEROW_TABS: TabDef[] = [
  {
    id: 'world',
    get label() {
      return t('tabWorld');
    },
    icon: 'land-plot',
  },
  { id: 'market', label: 'Market', icon: 'store' },
  { id: 'build', label: 'Build', icon: 'hammer' },
  // D95: the Almanac was a button inside Build's Machines segment; the co-op tab, one card
  // at stage 1, gives way to the region that is live from the first minute.
  { id: 'almanac', label: 'Almanac', icon: 'book' },
  {
    id: 'region',
    get label() {
      return t('tabRegion');
    },
    icon: 'users',
  },
  { id: 'ledger', label: 'Ledger', icon: 'book-open' },
];

export interface TabBarProps {
  active?: string;
  onChange?: (id: string) => void;
  badges?: Record<string, number | undefined>;
  /**
   * Walkthrough mark ids (D44). A tab whose `tab:<id>` is in here and that is not the
   * current one gets a small accent dot, so the next screen to open is visible from
   * wherever the player is.
   */
  marks?: ReadonlySet<string>;
  orientation?: 'bottom' | 'side';
  /**
   * D69: the side rail on a wide but short screen. Icons only, 56px wide, so a landscape
   * phone keeps its width for the world rather than giving a third of it to the tabs.
   */
  compact?: boolean;
  tabs?: TabDef[];
  style?: JSX.CSSProperties;
}

/** Six tabs (D95). Bottom bar on mobile (orientation="bottom"), left sidebar with labels on desktop ("side"). badges: {market: 3} */
export function TabBar({
  active = 'world',
  onChange,
  badges = {},
  marks,
  orientation = 'bottom',
  compact = false,
  tabs = HEDGEROW_TABS,
  style,
}: TabBarProps) {
  const side = orientation === 'side';
  return (
    <nav
      aria-label="Main"
      style={
        side
          ? {
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              width: compact ? 56 : 160,
              padding: 8,
              borderRight: '1px solid var(--line)',
              background: 'var(--surface)',
              ...style,
            }
          : {
              display: 'grid',
              gridTemplateColumns: `repeat(${tabs.length},1fr)`,
              height: 'calc(var(--tab-bar-h) + env(safe-area-inset-bottom))',
              paddingBottom: 'env(safe-area-inset-bottom)',
              background: 'var(--surface)',
              borderTop: '1px solid var(--line)',
              flex: 'none',
              ...style,
            }
      }
    >
      {tabs.map((t) => {
        const on = t.id === active;
        const b = on ? undefined : badges[t.id];
        const marked = !on && marks?.has(`tab:${t.id}`) === true;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange?.(t.id)}
            aria-current={on ? 'page' : undefined}
            aria-label={compact ? t.label : undefined}
            // What the walkthrough scrolls to and marks when a step is about this tab (D42).
            data-walk={`tab-${t.id}`}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: side && !compact ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: side && !compact ? 'flex-start' : 'center',
              gap: side && !compact ? 10 : 4,
              minHeight: 44,
              padding: side && !compact ? '0 10px' : 0,
              border: 0,
              borderRadius: side ? 'var(--radius-md)' : 0,
              background: on && side ? 'var(--surface-2)' : 'transparent',
              color: on ? 'var(--accent)' : 'var(--ink-2)',
              font: `${on ? 600 : 500} ${side ? 14 : 11}px/1 var(--font-ui)`,
              cursor: 'pointer',
            }}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Icon name={t.icon} size={side && !compact ? 18 : 22} />
              {b ? (
                <span
                  class="num"
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -10,
                    minWidth: 'var(--chip-h)',
                    height: 'var(--chip-h)',
                    padding: '0 4px',
                    borderRadius: 'var(--radius-round)',
                    background: 'var(--accent)',
                    color: 'var(--accent-ink)',
                    font: '700 var(--fs-badge)/18px var(--font-num)',
                    boxSizing: 'border-box',
                    boxShadow: '0 0 0 2px var(--surface)',
                    textAlign: 'center',
                  }}
                >
                  {b}
                </span>
              ) : null}
              {marked && (
                <span
                  data-testid={`tab-mark-${t.id}`}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -6,
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    background: 'var(--accent)',
                  }}
                />
              )}
            </span>
            {!compact && <span>{t.label}</span>}
          </button>
        );
      })}
    </nav>
  );
}
